import Http from '../../network/Http'
import {NatBaseResponseInterface} from '../../__NoAutomatedTests/components/common/repository/NatBaseResponseInterface'
import {Nullable} from '../../app/TypeHelpers'
import {ContentsForPagination} from '../shared/ContentsForPagination'
import {ContentType} from '../../network/Constants'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import {ApiResponse} from '../../network/response/ApiResponse'
import {AdminGeneralMeetingDetailInfo, AdminGeneralMeetingInfo} from './model/GeneralMeeting'
import {AVAILABLE_OR_NOT, GENERAL_MEETING_STATUS, RESOLUTION_TYPE, ROLE_FOR_ADMINISTRATOR} from '../../CodeMaster'
import {AdminGeneralMeetingFile} from './model/GeneralMeetingFile'
import {ProposalByAssoc} from './model/ProposalByAssoc'
import {VotingPreferenceSummaryByAssoc} from './model/VotingPreferenceSummaryByAssoc'
import {GeneralMeetingProposal} from './model/GeneralMeetingProposal'
import {QuestionSummaryByAssoc} from './model/QuestionSummaryByAssoc'
import {ConfirmationKeyNoticeData} from './model/ConfirmationKeyNoticeData'

export interface GetVotingRightsCountByAssocResponse extends NatBaseResponseInterface {
    details: Nullable<number>
}

export interface RoomsWithVotingRightsSearchCondition {
    ridgeCd: Nullable<string>
    roomNumber: Nullable<string>
    unitOwnerConfirmedStatus: string[]
    sortOrders: string[]
    rowsPerPage: number
    page: number
}

export interface RoomWithVotingRights {
    ridgeCd: string
    ridgeName: string
    roomNumber: string
    votingRightsCount: number
    unitOwnerSeq: Nullable<number>
    unitOwnerHasekoId: Nullable<string>
    unitOwnerName: Nullable<string>
    unitOwnerVersion: Nullable<number>
    votingRightsVersion: number
}

export interface GetRoomsWithVotingRightsResponse extends NatBaseResponseInterface {
    details: ContentsForPagination<RoomWithVotingRights>
}

export interface UpdateVotingRightsCount {
    assocId: number
    votingRightsCount: number
    version: number
}

export interface UpdateVotingRightsCountResponse extends NatBaseResponseInterface {
    details: null
}

export interface BulkUpdateVotingRightsCount {
    votingRightsBulkUpdateData: string
}

export interface BulkUpdateVotingRightsCountResponse extends NatBaseResponseInterface {
    details: null
}

export interface UpdateUnitOwnership {
    unitOwnership: Nullable<string>
    version: number
}

export interface UpdateUnitOwnershipResponse extends NatBaseResponseInterface {
    details: null
}

export interface GeneralMeetingFile {
    fileNumber: Nullable<number>
    fileName: string
    fileData: string
    fileSize: number
}

export interface GeneralMeetingStatusRequest {
    generalMeetingStatus: string
    version: number
}

export interface ProposalDetailInfoRequest {
    proposalNumber: number
    resolutionType: Nullable<string>
    proposalName: Nullable<string>
}

export interface ProposalByAssocRequest {
    assocId: number
    proposalDetailInfoList: ProposalDetailInfoRequest[]
}

export interface GeneralMeetingRequest {
    generalMeetingName: Nullable<string>
    assocIdList: number[]
    generalMeetingDate: Nullable<string>
    startTime: Nullable<string>
    votingPreferenceDeadline: Nullable<string>
    questionAvailability: string
    questionDeadline: Nullable<string>
    fileNameList: string[]
    proposalList: ProposalByAssocRequest[]
    noticeContent: Nullable<string>
    releaseDate: Nullable<string>
}

export interface GeneralMeetingRequestForUpdate extends GeneralMeetingRequest {
    generalMeetingStatusRequest: GeneralMeetingStatusRequest
    deleteFileNumberList: number[]
}

export interface GeneralMeetingsSearchCondition {
    assocId: Nullable<number>
    sortOrders: string[]
    rowsPerPage: number
    page: number
}

export interface GetGeneralMeetingsResponse extends NatBaseResponseInterface {
    details: ContentsForPagination<AdminGeneralMeetingInfo>
}

export interface GetGeneralMeetingInfoResponse extends NatBaseResponseInterface {
    details: AdminGeneralMeetingDetailInfo
}

export interface GetVotingPreferenceSummaryResponse extends NatBaseResponseInterface {
    details: VotingPreferenceSummaryByAssoc[]
}

export interface GetQuestionSummaryResponse extends NatBaseResponseInterface {
    details: QuestionSummaryByAssoc[]
}

export interface UpdateVotingPreferenceRequest {
    isSetManualClosedDatetime: boolean
    version: number
}

export interface GetConfirmationKeyNoticeDataResponse extends NatBaseResponseInterface {
    details: ConfirmationKeyNoticeData
}

export default class AdminGeneralMeetingRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async getVotingRightsCountByAssoc(
        condominiumCd: string,
        assocId: number,
    ): Promise<GetVotingRightsCountByAssocResponse> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/voting-rights-count`,
        )
    }

    async getRoomsWithVotingRights(
        condominiumCd: string,
        assocId: number,
        searchCondition: RoomsWithVotingRightsSearchCondition,
    ): Promise<GetRoomsWithVotingRightsResponse> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/rooms-with-voting-rights`,
            searchCondition,
            ContentType.JSON,
        )
    }

    async updateVotingRightsCount(
        condominiumCd: string,
        ridgeCd: string,
        roomNumber: string,
        requestParams: UpdateVotingRightsCount,
    ): Promise<UpdateVotingRightsCountResponse> {
        return await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/voting-rights-count`,
            requestParams,
        )
    }

    async bulkUpdateVotingRightsCount(
        condominiumCd: string,
        requestParam: BulkUpdateVotingRightsCount,
    ): Promise<BulkUpdateVotingRightsCountResponse> {
        return await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/voting-rights-count`,
            requestParam,
        )
    }

    async updateUnitOwnership(
        condominiumCd: string,
        ridgeCd: string,
        roomNumber: string,
        seq: number,
        requestParams: UpdateUnitOwnership,
    ): Promise<UpdateUnitOwnershipResponse> {
        return await this.http.put(
            // tslint:disable-next-line:max-line-length
            `/api/admin/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users/${seq}/unit-ownership`,
            requestParams,
        )
    }

    async registerGeneralMeeting(
        condominiumCd: string,
        isDraft: boolean,
        generalMeetingRequest: GeneralMeetingRequest,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/general-meetings?isDraft=${isDraft}`,
            generalMeetingRequest,
            ContentType.JSON,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async updateGeneralMeeting(
        condominiumCd: string,
        generalMeetingId: number,
        generalMeetingRequestForUpdate: GeneralMeetingRequestForUpdate,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
            generalMeetingRequestForUpdate,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async registerGeneralMeetingFile(
        condominiumCd: string,
        generalMeetingId: number,
        generalMeetingFile: GeneralMeetingFile,
    ): Promise<void> {
        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/file`,
            generalMeetingFile,
            ContentType.JSON,
        )
    }

    async updateGeneralMeetingStatus(
        condominiumCd: string,
        generalMeetingId: number,
        generalMeetingStatusRequest: GeneralMeetingStatusRequest,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/status`,
            generalMeetingStatusRequest,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async getGeneralMeetings(
        condominiumCd: string,
        searchCondition: GeneralMeetingsSearchCondition,
    ): Promise<GetGeneralMeetingsResponse> {
        const apiResponseJSON = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/search`,
            searchCondition,
            ContentType.JSON,
        )

        const apiResponse: GetGeneralMeetingsResponse = {
            ...apiResponseJSON,
            details: new ContentsForPagination(
                apiResponseJSON.details.totalContentsCount,
                apiResponseJSON.details.contents.map(
                    (detail: any): AdminGeneralMeetingInfo => ({
                        ...detail,
                        generalMeetingDate: detail.generalMeetingDate
                            ? new Date(detail.generalMeetingDate) : null,
                        generalMeetingStatus:
                            GENERAL_MEETING_STATUS.codeToEnumConstants(detail.generalMeetingStatus),
                        releaseDate: detail.releaseDate
                            ? new Date(detail.releaseDate) : null,
                    }),
                ),
            ),
        }

        return apiResponse
    }

    async getGeneralMeetingInfo(
        condominiumCd: string,
        generalMeetingId: number,
    ): Promise<GetGeneralMeetingInfoResponse> {
        const apiResponseJSON = await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`
        )

        const apiResponse: GetGeneralMeetingInfoResponse = {
            ...apiResponseJSON,
            details: {
                ...apiResponseJSON.details,
                generalMeetingDate: apiResponseJSON.details.generalMeetingDate
                    ? new Date(apiResponseJSON.details.generalMeetingDate) : null,
                generalMeetingStatus:
                    GENERAL_MEETING_STATUS.codeToEnumConstants(apiResponseJSON.details.generalMeetingStatus),
                releaseDate: apiResponseJSON.details.releaseDate
                    ? new Date(apiResponseJSON.details.releaseDate) : null,
                votingPreferenceDeadline: apiResponseJSON.details.votingPreferenceDeadline
                    ? new Date(apiResponseJSON.details.votingPreferenceDeadline) : null,
                votingPreferenceManualClosedDatetime: apiResponseJSON.details.votingPreferenceManualClosedDatetime
                    ? new Date(apiResponseJSON.details.votingPreferenceManualClosedDatetime) : null,
                questionAvailability:
                    AVAILABLE_OR_NOT.codeToEnumConstants(apiResponseJSON.details.questionAvailability),
                questionDeadline: apiResponseJSON.details.questionDeadline
                    ? new Date(apiResponseJSON.details.questionDeadline) : null,
                cancelDatetime: apiResponseJSON.details.cancelDatetime
                    ? new Date(apiResponseJSON.details.cancelDatetime) : null,
                fileList: apiResponseJSON.details.fileList.map(
                    (generalMeetingFile: any): AdminGeneralMeetingFile => ({
                        ...generalMeetingFile,
                        registeredDatetime: new Date(generalMeetingFile.registeredDatetime),
                        registeredUserRoleId: generalMeetingFile.registeredUserRoleId
                            ? ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(generalMeetingFile.registeredUserRoleId)
                            : null,
                    }),
                ),
                proposalList: apiResponseJSON.details.proposalList.map(
                    (proposalByAssoc: any): ProposalByAssoc => ({
                        ...proposalByAssoc,
                        generalMeetingProposalList:
                            proposalByAssoc.generalMeetingProposalList.map(
                                (proposalDetail: any): GeneralMeetingProposal => ({
                                    ...proposalDetail,
                                    resolutionType: proposalDetail.resolutionType
                                        ? RESOLUTION_TYPE.codeToEnumConstants(proposalDetail.resolutionType) : null,
                                }),
                            ),
                    }),
                ),
            }
        }

        return apiResponse
    }

    async getVotingPreferenceSummary(
        condominiumCd: string,
        generalMeetingId: number,
    ): Promise<GetVotingPreferenceSummaryResponse> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/voting-preference-summary`
        )
    }

    getVotingPreferenceResultExcel(
        condominiumCd: string,
        generalMeetingId: number,
    ): string {
        return `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/voting-preference-result`
    }

    async getQuestionSummary(
        condominiumCd: string,
        generalMeetingId: number,
    ): Promise<GetQuestionSummaryResponse> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/question-summary`
        )
    }

    getQuestionCsv(
        condominiumCd: string,
        generalMeetingId: number,
    ): string {
        return `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/question`
    }

    async updateVotingPreferenceManualDeadline(
        condominiumCd: string,
        generalMeetingId: number,
        updateVotingPreferenceRequest: UpdateVotingPreferenceRequest
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/voting-preference-manual-closed-datetime`,
            updateVotingPreferenceRequest,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async deleteGeneralMeeting(
        condominiumCd: string,
        generalMeetingId: number,
        version: number,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.delete(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}?version=${version}`,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async getConfirmationKeyNoticeData(
        condominiumCd: string
    ): Promise<GetConfirmationKeyNoticeDataResponse> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/general-meetings/confirmation-key-notice`
        )
    }
}
